import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Search, Loader2, CalendarIcon, X, Star, RotateCcw, ArrowLeftRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { LocationAutocomplete } from "@/components/ui/location-autocomplete";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilterState, getDefaultFilterState } from "./SearchFilters";
import { clampRadiusValue } from "@/lib/loadSearch";
import { SaveSearchModal } from "./SaveSearchModal";
import { SavedSearchesTabStrip } from "./SavedSearchesTabStrip";

import type { SearchTabFilters } from "./ActiveSearchTabs";

export interface SearchFilterParams {
  origin: string;
  destination: string;
  equipmentTypes: string[];
  dhoRadius: number;
  dhdRadius: number;
  loadType: "full" | "partial" | "both";
  hideCanada: boolean;
  searchBack: number;
  minWeight: number | null;
  minLength: number | null;
  pickupDateFrom: string | null;
  pickupDateTo: string | null;
}

interface SearchFilterBarProps {
  onSearch: (params: SearchFilterParams) => void;
  isSearching?: boolean;
  isRefreshing?: boolean;
  resultCount?: number;
  filterValues?: SearchTabFilters;
  onFilterChange?: (filters: SearchTabFilters) => void;
}

/* ─── tiny field wrapper matching Quick Post cells ──────────────────── */
function FilterCell({
  label,
  required,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-0 px-2 py-0.5", className)}>
      <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </span>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="hidden md:block w-px bg-border/80 self-stretch shrink-0 my-1" />;
}

/* ─── helper: equipment short label ──────────────────────────────── */
function getEquipShortLabel(enumVal: string): string {
  const found = EQUIPMENT_FLAT_LIST.find((t) => t.enumValue === enumVal);
  return found ? found.name : enumVal;
}

/* ─── Active Filter Chips row ────────────────────────────────────── */
function ActiveFilterChips({
  origin, destination, equipmentTypes, loadType, minLength, minWeight,
  pickupDate, hideCanada, dhoRadius, dhdRadius, searchBack, onRemove,
}: {
  origin: string; destination: string; equipmentTypes: string[];
  loadType: string; minLength: string; minWeight: string;
  pickupDate: Date | undefined; hideCanada: boolean;
  dhoRadius: string; dhdRadius: string; searchBack: string;
  onRemove: (key: string, value?: string) => void;
}) {
  const chips: { key: string; label: string; value?: string }[] = [];

  if (origin.trim()) chips.push({ key: "origin", label: `Origin: ${origin}` });
  if (destination.trim()) chips.push({ key: "destination", label: `Dest: ${destination}` });
  equipmentTypes.forEach((v) => chips.push({ key: "equipment", label: getEquipShortLabel(v), value: v }));
  if (loadType !== "full") chips.push({ key: "loadType", label: `Load: ${loadType}` });
  if (minLength) chips.push({ key: "minLength", label: `Length ≥ ${minLength} ft` });
  if (minWeight) chips.push({ key: "minWeight", label: `Weight ≥ ${minWeight} lbs` });
  if (pickupDate) chips.push({ key: "pickupDate", label: `Pickup: ${format(pickupDate, "MMM d")}` });
  if (hideCanada) chips.push({ key: "hideCanada", label: "Hide Canada" });
  if (dhoRadius && dhoRadius !== "150") chips.push({ key: "dhoRadius", label: `DH-O: ${dhoRadius} mi` });
  if (dhdRadius && dhdRadius !== "150") chips.push({ key: "dhdRadius", label: `DH-D: ${dhdRadius} mi` });
  if (searchBack && searchBack !== "7") chips.push({ key: "searchBack", label: `Within ${searchBack} days` });

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-1">
      {chips.map((chip, i) => (
        <span
          key={`${chip.key}-${chip.value ?? i}`}
          className="h-6 px-2 text-xs rounded-sm bg-muted inline-flex items-center gap-1 text-foreground"
        >
          {chip.label}
          <X
            className="h-3 w-3 cursor-pointer text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(chip.key, chip.value)}
          />
        </span>
      ))}
    </div>
  );
}

export function SearchFilterBar({
  onSearch,
  isSearching,
  isRefreshing,
  resultCount,
  filterValues,
  onFilterChange,
}: SearchFilterBarProps) {
  const { profile } = useAuth();
  const isBroker = profile?.user_type === "broker";

  // ── primary filters ──
  const [origin, setOrigin] = useState(filterValues?.origin ?? "");
  const [destination, setDestination] = useState(filterValues?.destination ?? "");
  const [equipmentTypes, setEquipmentTypes] = useState<string[]>(filterValues?.equipmentTypes ?? []);
  const [dhoRadius, setDhoRadius] = useState<string>(filterValues?.dhoRadius ?? "150");
  const [dhdRadius, setDhdRadius] = useState<string>(filterValues?.dhdRadius ?? "150");
  const [loadType, setLoadType] = useState<"full" | "partial" | "both">((filterValues?.loadType as "full" | "partial" | "both") ?? "full");
  const [minLength, setMinLength] = useState<string>(filterValues?.minLength ?? "");
  const [minWeight, setMinWeight] = useState<string>(filterValues?.minWeight ?? "");
  const [pickupDate, setPickupDate] = useState<Date | undefined>(
    filterValues?.pickupDate ? parseISO(filterValues.pickupDate) : undefined
  );

  // ── secondary filters ──
  const [hideCanada, setHideCanada] = useState(filterValues?.hideCanada ?? false);
  const [searchBack, setSearchBack] = useState<string>(filterValues?.searchBack ?? "7");

  // ── Stable ref for onFilterChange to break re-render loop ──
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  // ── Sync filter changes up to parent ──
  useEffect(() => {
    onFilterChangeRef.current?.({
      origin,
      destination,
      dhoRadius,
      dhdRadius,
      equipmentTypes,
      loadType,
      minLength,
      minWeight,
      pickupDate: pickupDate ? format(pickupDate, "yyyy-MM-dd") : null,
      hideCanada,
      searchBack,
      sortBy: filterValues?.sortBy ?? "age_newest",
    });
  }, [origin, destination, dhoRadius, dhdRadius, equipmentTypes, loadType, minLength, minWeight, pickupDate, hideCanada, searchBack]);

  // ── validation ──
  const [locationError, setLocationError] = useState(false);
  const [equipmentError, setEquipmentError] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  // ── modals / panels ──
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const buildParams = useCallback((): SearchFilterParams => {
    return {
      origin,
      destination,
      equipmentTypes,
      dhoRadius: clampRadiusValue(dhoRadius, 150),
      dhdRadius: clampRadiusValue(dhdRadius, 150),
      loadType,
      hideCanada,
      searchBack: parseInt(searchBack) || 7,
      minWeight: minWeight ? parseInt(minWeight) : null,
      minLength: minLength ? parseInt(minLength) : null,
      pickupDateFrom: pickupDate ? format(pickupDate, "yyyy-MM-dd") : null,
      pickupDateTo: null,
    };
  }, [origin, destination, equipmentTypes, dhoRadius, dhdRadius, loadType, hideCanada, searchBack, minWeight, minLength, pickupDate]);

  const handleSearch = () => {
    setLocationError(false);
    setEquipmentError(false);
    setValidationMsg("");

    if (isBroker) {
      const missingOrigin = !origin.trim();
      const missingDest = !destination.trim();
      const missingEquip = equipmentTypes.length === 0;
      if (missingOrigin || missingDest || missingEquip) {
        if (missingOrigin || missingDest) setLocationError(true);
        if (missingEquip) setEquipmentError(true);
        const missing: string[] = [];
        if (missingOrigin) missing.push("Origin");
        if (missingDest) missing.push("Destination");
        if (missingEquip) missing.push("Equipment");
        setValidationMsg(`${missing.join(", ")} required for broker search`);
        return;
      }
    } else {
      if (!origin.trim() && !destination.trim()) {
        setLocationError(true);
        setValidationMsg("Origin or Destination required");
        return;
      }
    }

    const params = buildParams();
    onSearch(params);
  };

  const handleClear = () => {
    setOrigin("");
    setDestination("");
    setEquipmentTypes([]);
    setDhoRadius("150");
    setDhdRadius("150");
    setLoadType("full");
    setMinLength("");
    setMinWeight("");
    setPickupDate(undefined);
    setHideCanada(false);
    setSearchBack("7");
    setLocationError(false);
    setEquipmentError(false);
    setValidationMsg("");
  };

  const clampRadius = (val: string): string => {
    const digits = val.replace(/\D/g, "");
    if (!digits) return "";
    const n = Math.min(Math.max(parseInt(digits, 10), 1), 475);
    return String(n);
  };

  const handleSwapLane = () => {
    const prevOrigin = origin;
    const prevDho = dhoRadius;
    setOrigin(destination);
    setDestination(prevOrigin);
    setDhoRadius(dhdRadius);
    setDhdRadius(prevDho);
  };

  const handleLoadSavedSearch = useCallback(
    (state: FilterState) => {
      setOrigin(state.origin);
      setDestination(state.destination);
      setDhoRadius(String(clampRadiusValue(state.dhoRadius, 150)));
      setDhdRadius(String(clampRadiusValue(state.dhdRadius, 150)));
      setEquipmentTypes(state.selectedEquipment);
      setLoadType(state.loadType === "partial" ? "partial" : "full");
      setHideCanada(state.hideCanada);
      setSearchBack(String(state.searchBack));
      if (state.dateRange.length > 0) setPickupDate(state.dateRange[0]);

      // auto-trigger search after loading
      setTimeout(() => {
        onSearch({
          origin: state.origin,
          destination: state.destination,
          equipmentTypes: state.selectedEquipment,
          dhoRadius: clampRadiusValue(state.dhoRadius, 150),
          dhdRadius: clampRadiusValue(state.dhdRadius, 150),
          loadType: state.loadType === "partial" ? "partial" : "full",
          hideCanada: state.hideCanada,
          searchBack: state.searchBack,
          minWeight: null,
          minLength: null,
          pickupDateFrom:
            state.dateRange.length > 0
              ? format(state.dateRange[0], "yyyy-MM-dd")
              : null,
          pickupDateTo: null,
        });
      }, 50);
    },
    [onSearch],
  );

  // build FilterState for SaveSearchModal interop
  const currentFilterState: FilterState = {
    origin,
    destination,
    dhoRadius: clampRadiusValue(dhoRadius, 150),
    dhdRadius: clampRadiusValue(dhdRadius, 150),
    selectedEquipment: equipmentTypes,
    loadType: loadType === "both" ? "full" : loadType,
    dateRange: pickupDate ? [pickupDate] : [new Date()],
    hideCanada,
    searchBack: parseInt(searchBack) || 7,
  };

  const inputBase =
    "h-5 text-[11px] border-0 bg-transparent p-0 shadow-none focus-visible:ring-0";

  return (
    <div className="flex flex-col gap-1 w-full bg-background pb-1">
      {/* ═══ Saved Searches Tab Strip ═══ */}
      <SavedSearchesTabStrip
          onLoadSearch={handleLoadSavedSearch}
          refreshTrigger={refreshTrigger}
        />

      {/* ═══ ROW 1 — Primary Filters ═══ */}
      <div
        className={cn(
          "flex flex-col md:flex-row md:flex-wrap items-stretch md:items-end gap-2 md:gap-0 p-2 md:p-0 rounded-lg border border-border bg-card shadow-sm",
        )}
      >
        {/* Origin */}
        <FilterCell label="Origin" className="w-full md:w-auto md:min-w-[80px] md:flex-[0.64]">
          <LocationAutocomplete
            value={origin}
            onChange={(v) => { setOrigin(v); setLocationError(false); }}
            placeholder="City, ST"
            className={cn(inputBase, "truncate", locationError && !origin.trim() && "ring-1 ring-destructive rounded")}
          />
        </FilterCell>

        <Divider />

        {/* DH-O */}
        <FilterCell label="DH-O (mi)" className="w-full md:w-[80px]">
          <Input
            type="text"
            inputMode="numeric"
            value={dhoRadius}
            onChange={(e) => setDhoRadius(clampRadius(e.target.value))}
            onBlur={() => { if (dhoRadius) setDhoRadius(String(clampRadiusValue(dhoRadius, 150))); }}
            placeholder="150"
            className={cn(inputBase, "w-full")}
          />
        </FilterCell>

        {/* Swap Lane */}
        <div className="hidden md:flex self-center shrink-0 px-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleSwapLane}
            className="h-5 w-5 text-muted-foreground hover:text-foreground"
            title="Swap origin and destination"
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Destination */}
        <FilterCell label="Destination" className="w-full md:w-auto md:min-w-[80px] md:flex-[0.64]">
          <LocationAutocomplete
            value={destination}
            onChange={(v) => { setDestination(v); setLocationError(false); }}
            placeholder="City, ST"
            className={cn(inputBase, "truncate", locationError && !destination.trim() && "ring-1 ring-destructive rounded")}
          />
        </FilterCell>

        <Divider />

        {/* DH-D */}
        <FilterCell label="DH-D (mi)" className="w-full md:w-[80px]">
          <Input
            type="text"
            inputMode="numeric"
            value={dhdRadius}
            onChange={(e) => setDhdRadius(clampRadius(e.target.value))}
            onBlur={() => { if (dhdRadius) setDhdRadius(String(clampRadiusValue(dhdRadius, 150))); }}
            placeholder="150"
            className={cn(inputBase, "w-full")}
          />
        </FilterCell>

        <Divider />

        {/* Equipment */}
        <FilterCell label="Equipment" required className="w-full md:w-[252px] md:max-w-[288px]">
          <EquipmentSelect
            value={equipmentTypes}
            onChange={(v) => { setEquipmentTypes(Array.isArray(v) ? v : [v]); setEquipmentError(false); }}
            mode="multi"
            className={cn(inputBase, equipmentError && "ring-1 ring-destructive rounded")}
          />
        </FilterCell>

        <Divider />

        {/* Load Type */}
        <FilterCell label="Load Type" className="w-full md:w-[110px]">
          <Select
            value={loadType}
            onValueChange={(v) => setLoadType(v as "full" | "partial" | "both")}
          >
            <SelectTrigger className={cn(inputBase, "w-full border-0 bg-transparent shadow-none px-0 h-5 text-[11px]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Full</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </FilterCell>

        <Divider />

        {/* Length */}
        <FilterCell label="Length (ft)" className="w-full md:w-[96px]">
          <Input
            type="text"
            inputMode="numeric"
            value={minLength}
            onChange={(e) => setMinLength(e.target.value.replace(/\D/g, ""))}
            placeholder="Any"
            className={cn(inputBase, "w-full")}
          />
        </FilterCell>

        <Divider />

        {/* Weight */}
        <FilterCell label="Weight (lbs)" className="w-full md:w-[104px]">
          <Input
            type="text"
            inputMode="numeric"
            value={minWeight}
            onChange={(e) => setMinWeight(e.target.value.replace(/\D/g, ""))}
            placeholder="Any"
            className={cn(inputBase, "w-full")}
          />
        </FilterCell>

        <Divider />

        {/* Pickup Date */}
        <FilterCell label="Pickup Date" className="w-full md:w-[138px]">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "h-5 px-0 text-[11px] font-normal justify-start gap-1 hover:bg-transparent",
                  !pickupDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="h-3 w-3 opacity-50" />
                {pickupDate ? format(pickupDate, "MMM d") : "Any"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[10050]" align="start">
              <Calendar
                mode="single"
                selected={pickupDate}
                onSelect={setPickupDate}
                fromDate={new Date()}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </FilterCell>

        {/* Search Button */}
        <div className="px-0 md:px-2 py-1 md:py-0.5 flex items-center w-full md:w-auto">
          <Button
            onClick={handleSearch}
            disabled={isSearching}
            className="rounded-lg bg-brand-teal text-white font-semibold px-6 hover:bg-brand-teal/90 shrink-0 w-full md:w-auto h-10 md:h-9"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Search Loads
          </Button>
        </div>
      </div>

      {/* ═══ Inline validation error ═══ */}
      {validationMsg && (
        <p className="text-[11px] font-medium text-destructive px-3 -mt-1">
          {validationMsg}
        </p>
      )}

      {/* ═══ ROW 2 — Secondary Controls ═══ */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-1 text-[11px]">
        {/* Hide Canada */}
        <label className="flex items-center gap-1.5 text-muted-foreground cursor-pointer">
          <Switch
            checked={hideCanada}
            onCheckedChange={setHideCanada}
            className="h-4 w-8 [&>span]:h-3 [&>span]:w-3 data-[state=checked]:[&>span]:translate-x-4 data-[state=checked]:bg-red-600"
          />
          Hide Canada
        </label>

        {/* Search Back */}
        <label className="flex items-center gap-1.5 text-muted-foreground">
          Posted within
          <Input
            type="number"
            min={1}
            max={30}
            value={searchBack}
            onChange={(e) => setSearchBack(e.target.value)}
            className="h-6 w-12 text-[11px] px-1 text-center rounded border border-border bg-background"
          />
          days
        </label>

        {/* Result count */}
        {resultCount !== undefined && (
          <span className="text-muted-foreground">
            {resultCount} result{resultCount !== 1 ? "s" : ""}
          </span>
        )}

        {isRefreshing && (
          <span className="text-[10px] font-medium text-brand-teal animate-pulse">
            Refreshing…
          </span>
        )}

        <div className="flex-1" />

        {/* Clear Filters */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
        >
          <RotateCcw className="h-3 w-3" />
          Clear
        </Button>

        {/* Save Search */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsSaveModalOpen(true)}
          className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
        >
          <Star className="h-3 w-3" />
          Save Search
        </Button>
      </div>

      {/* ═══ ROW 3 — Active Filter Chips ═══ */}
      <ActiveFilterChips
        origin={origin}
        destination={destination}
        equipmentTypes={equipmentTypes}
        loadType={loadType}
        minLength={minLength}
        minWeight={minWeight}
        pickupDate={pickupDate}
        hideCanada={hideCanada}
        dhoRadius={dhoRadius}
        dhdRadius={dhdRadius}
        searchBack={searchBack}
        onRemove={(key, value) => {
          switch (key) {
            case "origin": setOrigin(""); break;
            case "destination": setDestination(""); break;
            case "equipment": setEquipmentTypes(equipmentTypes.filter(v => v !== value)); break;
            case "loadType": setLoadType("full"); break;
            case "minLength": setMinLength(""); break;
            case "minWeight": setMinWeight(""); break;
            case "pickupDate": setPickupDate(undefined); break;
            case "hideCanada": setHideCanada(false); break;
            case "dhoRadius": setDhoRadius("150"); break;
            case "dhdRadius": setDhdRadius("150"); break;
            case "searchBack": setSearchBack("7"); break;
          }
          setTimeout(() => handleSearch(), 50);
        }}
      />

      {/* ═══ Save Search Modal ═══ */}
      <SaveSearchModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        filterState={currentFilterState}
        onSave={() => setRefreshTrigger((t) => t + 1)}
      />
    </div>
  );
}
